// ColorPanel — layout 非依存の共有カラーパネル本体 (consult 2026-06-20).
//
// 由来: 旧 `layouts/modern/ModernColorPanel` の本体 (色相リング + 内接正方形SV + 現在色
// プレビュー + HEX 入力 + RGB/HSV/HSL の 4way チャンネルバー数値) をそのまま抽出し、
//
// ## カラーホイールの構成 (consult 2026-06-21: CSP/Java 互換に変更)
//   - 旧: @uiw `Wheel` (色相環 + 中央円形 S/V) + `ShadeSlider` (明度 V)。V=0 始まりだと
//     色相を選んでも黒のままになる問題があり `seedWheelHsva` hack で対症していた。
//   - 新: 外側 **色相リング (自前 HueRing, conic-gradient + pointer 角度)** + 中央 **内接正方形
//     SV (@uiw `Saturation`、横=S・縦=V)**。正方形の角で黒(左下)・白(左上)・純色(右上) が一発で
//     取れる業界標準 UI。明度は正方形の縦軸に内包するので `ShadeSlider` は撤去。V=0 でも
//     正方形は常に操作可能なので `seedWheelHsva` hack も撤去。
// **Classic / Modern 両 layout が共有**する核に昇格させたもの (consult 2026-06-20:
// 「Classic を見た目含め Modern に寄せる / Classic の核は dblclick→popup という操作流儀」)。
//
// ## 配置 (= シェル側が担う、本体は size に寛容)
//   - Modern: 右ドックに常設 (`ModernColorPanel` 薄 wrapper 経由)。
//   - Classic: パレット dblclick で開く floating popup (`ColorWheelPalette`) の中身。
//   両 layout でこの ColorPanel を使い、外枠 (ドック / フロート) だけシェルが変える。
//
// ## 不透明度 (alpha) はここに置かない (consult 2026-06-08 / 2026-06-20)
//   - 本パネルは alpha の **操作 UI を持たない** (`onAlphaChange` を発射しない)。
//   - Modern: alpha はツールプロパティ (ModernToolProperties) が唯一の操作元 (§9.1 決定3)。
//   - Classic: alpha は左ペイン常設の `ArgbSliderPanel` (A/R/G/B スライダの A) が担う。
//     => 共有 ColorPanel から alpha が消えても、両 layout とも alpha 操作能力は失わない。
//   - 背景色エディタ用途は alpha 固定 255 なので alpha なしパネルがちょうど適合する。
//
// ## 現在色プレビュー (スウォッチ) は実色表示 (= alpha を視覚表現しない、α feedback 2026-07-08)
//   - 旧仕様は `opacity: value.alpha255 / 255` をスウォッチに適用しており、ブラシ不透明度が
//     低いとスウォッチがパネル背景に溶けて「今選んでいる色」が判別できない不具合があった
//     (英語ユーザ報告: 黒を選んでも不透明度が低いと黒く見えない)。
//   - 対策: スウォッチは常に **`currentHex` の実色** で表示し、`opacity` は適用しない。
//     CSP / Photoshop 等の主要ソフトも現在色スウォッチに alpha を掛けない流儀で、要望とも一致する。
//   - alpha を操作する能力自体は失われない (Classic = `ArgbSliderPanel` の A スライダ、
//     Modern = `ModernToolProperties` が引き続き担う)。スウォッチが alpha を**表示しなくなる**
//     だけ。
//
// 駆動: `colorWheel.ts` の純変換 (`hsvaFromRgb24Alpha255` / `rgb24FromHsva`) で
// PenConfig `{color: rgb24}` を駆動する。内部 state は HsvaColor 1 個 (single source of
// truth)。`hsva.a` は本パネルの表示には使わない (スウォッチも実色固定) が、外部
// `value.alpha255` を型 (`HsvaColor.a` は必須 field) の整合のため内部 state に取り込み続ける
// (色の commit でも `value.alpha255` を据え置き、alpha は決して自分から動かさない)。外部 value
// 変更 (= スポイト/パレットスロット選択/alpha の外部更新) には controlled sync で追従し、
// 自分の echo は skip する (`shouldSyncFromExternalValue`)。
//
// ## 数値表記の切替 (HEX / RGB / HSV / HSL、consult 2026-06-08)
//   - ホイール下に **ホイール / RGB / HSV / HSL の 4 way タブ**。HEX 入力はプレビュー行に
//     一本化 (タブには持たない)。色成分だけ編集 (A 欄は出さない)。
//   - RGB/HSV/HSL は各チャンネルが「そのチャンネルの色グラデを背景に持つ横バー」
//     (native `<input type="range">`)。グラデ計算は `colorChannelBars.ts` の純関数、
//     クロスブラウザ thumb/track styling は `styles.css` の `.modern-channel-bar`。

import { Saturation } from '@uiw/react-color';
import { useEffect, useId, useRef, useState } from 'react';

import { buildChannelBars } from './colorChannelBars.js';
import {
  type DisplayValues,
  type HsvaColor,
  hexFromHsva,
  hsvaFromDisplayValues,
  hsvaFromRgb24Alpha255,
  hsvFromHex,
  NUMERIC_MODE_LABELS,
  NUMERIC_MODES,
  type NumericMode,
  rgb24FromHsva,
  shouldSyncFromExternalValue,
  toDisplayValues,
} from './colorWheel.js';
import { HueRing } from './HueRing.js';

/** 入力ビューの切替タブ (ホイール + RGB/HSV/HSL の 4 way)。
 *  - `'wheel'`: 色相リング + 内接正方形SV (横=S・縦=V) のビュー。
 *  - RGB/HSV/HSL: colorWheel の NumericMode (チャンネルバー群のビュー)。
 *  HEX は **プレビュー行の編集可能 input に一本化**したので、タブには持たない。 */
type DisplayMode = 'wheel' | NumericMode;
const DISPLAY_MODES: ReadonlyArray<DisplayMode> = ['wheel', ...NUMERIC_MODES];
const DISPLAY_MODE_LABELS: Readonly<Record<DisplayMode, string>> = {
  wheel: 'ホイール',
  ...NUMERIC_MODE_LABELS,
};

/** 入力ビュースロットの固定高 (px)。ホイールビューと RGB/HSV/HSL バービューで
 *  **パネル総高が変わらない**ように、大きい方 (= ホイールビュー = 色相リング 160px) の
 *  自然高に合わせて固定。 */
const VIEW_SLOT_MIN_HEIGHT = 170;

export interface ColorPanelProps {
  /**
   * 現在の描画色 (controlled)。外部 (スポイト/パレットスロット選択/alpha 外部更新) 変更に追従する。
   * `alpha255` は本パネルの表示には使わない (= スウォッチは常に実色、opacity を適用しない、
   * α feedback 2026-07-08)。本パネルは alpha を操作しないため、`value.alpha255` は
   * Classic/Modern 呼び出し側 API 互換のために引数として保持しているだけの pass-through。
   */
  readonly value: { readonly rgb24: number; readonly alpha255: number };
  /** 色変更 (= pen.color)。wheel / shade / hex / 数値編集で即発火。 */
  readonly onColorChange: (rgb24: number) => void;
  /**
   * パネル先頭に「カラー」見出しを出すか (default true)。
   * Classic floating popup はフロート枠側に既にタイトルバー (「カラーホイール」) を持つので、
   * 二重見出しを避けるため `false` を渡して見出しを抑制する。
   */
  readonly showHeader?: boolean;
}

/**
 * 共有カラーパネル本体 (HSV ホイール + 明度 + プレビュー + HEX + 4way チャンネルバー)。
 * size に寛容: フロート枠 (Classic) / ドック枠 (Modern) どちらに置いても破綻しないよう、
 * 幅は親に従う (`w-full` 相当) 余白小の縦積み。
 */
export const ColorPanel = ({
  value,
  onColorChange,
  showHeader = true,
}: ColorPanelProps): React.ReactNode => {
  // 内部 source-of-truth は HsvaColor 1 個。色相リング + 内接正方形SV なので V=0(黒) 始まり
  // でも正方形の縦軸で常に操作可能 = 旧 seedWheelHsva hack は不要 (= 黒は黒のまま保つ)。
  const [hsva, setHsva] = useState<HsvaColor>(() =>
    hsvaFromRgb24Alpha255(value.rgb24, value.alpha255),
  );
  const [hexInput, setHexInput] = useState<string>(() => hexFromHsva(hsva));
  const [hexComposing, setHexComposing] = useState<boolean>(false);

  // 入力ビュー切替タブ (ホイール / RGB / HSV / HSL)。既定はホイールビュー。
  const [displayMode, setDisplayMode] = useState<DisplayMode>('wheel');

  // 自分発射の echo skip 用。null 始まりで、マウント直後の外部 value はそのまま取り込む
  // (= 黒 #000000 を黒として扱う、hack なし)。
  const lastEmittedRef = useRef<{ rgb24: number; alpha255: number } | null>(null);

  // 外部 value 変更 (= スポイト / 親 state 変更 / alpha 外部更新) に追従。echo なら skip。
  // alpha は操作 UI が無いので、外部 alpha の変化も内部 hsva.a へ取り込んでおく (= state の
  // 整合性維持。スウォッチは実色固定でこの値を表示に使わないが、hsva の型が a を必須で持つため
  // 常に外部 value.alpha255 と一致させておく)。
  // deps は **値 (rgb24 / alpha255)** であってオブジェクト参照ではない: 呼び出し側は毎レンダー
  // 新しい `{ rgb24, alpha255 }` を渡す (ModernLayout / ClassicLayout)。参照を deps にすると
  // 親が再レンダーするたび (= imagepaste プレビューのドラッグ中は pointermove ごと) に
  // setHsva(新オブジェクト) が走り、親の再レンダー連鎖に加担して更新深度の上限を踏む。
  const externalRgb24 = value.rgb24;
  const externalAlpha255 = value.alpha255;
  useEffect(() => {
    const external = { rgb24: externalRgb24, alpha255: externalAlpha255 };
    if (!shouldSyncFromExternalValue(external, lastEmittedRef.current)) return;
    setHsva(hsvaFromRgb24Alpha255(externalRgb24, externalAlpha255));
    lastEmittedRef.current = null;
  }, [externalRgb24, externalAlpha255]);

  // hsva → hex 表示同期 (逆向きは tryCommitHex)。
  useEffect(() => {
    setHexInput(hexFromHsva(hsva));
  }, [hsva]);

  /**
   * 色変更を commit (= hsva 更新 + 親へ色だけ発射)。alpha は本パネルでは動かさないので、
   * hsva.a / lastEmitted.alpha255 は **現在の外部 `value.alpha255`** に固定する
   * (= 呼び出し元が `nextColor.a` に何を渡していても、ここで必ず上書きされる)。
   */
  const commitColor = (nextColor: HsvaColor): void => {
    const a = value.alpha255 / 255;
    const next: HsvaColor = { ...nextColor, a };
    setHsva(next);
    const rgb24 = rgb24FromHsva(next);
    lastEmittedRef.current = { rgb24, alpha255: value.alpha255 };
    onColorChange(rgb24);
  };

  const tryCommitHex = (raw: string): void => {
    const text = raw.trim();
    const withHash = text.startsWith('#') ? text : `#${text}`;
    const parsed = hsvFromHex(withHash);
    if (parsed === null) {
      setHexInput(hexFromHsva(hsva));
      return;
    }
    commitColor({ ...parsed, a: hsva.a });
  };

  // 数値表記 (RGB/HSV/HSL) の 1 成分編集を HSVA に書き戻す。alpha は hsva.a を保持。
  const commitDisplay = (next: DisplayValues): void => {
    commitColor(hsvaFromDisplayValues(next, hsva.a));
  };

  const hexInputId = useId();
  const currentHex = hexFromHsva(hsva);

  return (
    <section
      className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-2"
      aria-label="カラー"
      data-manual="color.wheel"
    >
      {showHeader && (
        <header className="select-none text-[11px] font-bold text-[var(--color-text-muted)]">
          カラー
        </header>
      )}

      {/* 1. 現在色プレビュー (常時)。スウォッチは常に **実色** (= alpha を適用しない、
          α feedback 2026-07-08) で表示する。隣に **唯一の編集可能 HEX 入力**を inline で
          置く。IME 中は commit せず、Enter / blur で tryCommitHex。 */}
      <div className="flex items-center gap-2">
        <div
          role="img"
          aria-label="現在の色"
          className="h-7 w-7 shrink-0 rounded border border-[var(--color-border)]"
          style={{ backgroundColor: currentHex }}
        />
        <input
          id={hexInputId}
          value={hexInput}
          onChange={(e) => setHexInput(e.currentTarget.value)}
          onCompositionStart={() => setHexComposing(true)}
          onCompositionEnd={() => setHexComposing(false)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            if (hexComposing || e.nativeEvent.isComposing) return;
            e.preventDefault();
            tryCommitHex(e.currentTarget.value);
          }}
          onBlur={(e) => tryCommitHex(e.currentTarget.value)}
          className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-1.5 py-0.5 font-mono text-[11px] text-white outline-none focus:border-[var(--color-accent)]"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          aria-label="HEX 色コード"
        />
      </div>

      {/* 2. 入力ビュー切替タブ (ホイール / RGB / HSV / HSL、常時)。 */}
      <div
        className="flex gap-0.5"
        role="tablist"
        aria-label="数値表記"
        data-manual="color.sliders"
      >
        {DISPLAY_MODES.map((m) => {
          const selected = displayMode === m;
          return (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setDisplayMode(m)}
              className={`flex-1 rounded border px-1 py-0.5 text-[10px] ${
                selected
                  ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-panel)]'
                  : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]'
              }`}
            >
              {DISPLAY_MODE_LABELS[m]}
            </button>
          );
        })}
      </div>

      {/* 3. 入力ビュー (排他)。同じスロットを ホイール⇔RGB/HSV/HSL で入れ替え。
          スロットは **固定 min 高 (VIEW_SLOT_MIN_HEIGHT)** にして、入れ替えでもパネル総高
          が変わらないようにする。 */}
      <div className="flex flex-col" style={{ minHeight: VIEW_SLOT_MIN_HEIGHT }}>
        {displayMode === 'wheel' ? (
          // 色相リング (外周ドラッグで H) + 内接正方形SV (= @uiw Saturation、横=S・縦=V)。
          // Saturation は `hue` prop で背景を描き、onChange で h を保持したまま s/v を返す
          // ので、H はリング・S/V は正方形が独立に駆動する。
          <div className="flex justify-center py-1">
            <HueRing hue={hsva.h} onHueChange={(h) => commitColor({ ...hsva, h })}>
              <Saturation
                hsva={hsva}
                hue={hsva.h}
                style={{ width: '100%', height: '100%', borderRadius: 4 }}
                onChange={(c) => commitColor({ ...c, a: hsva.a })}
              />
            </HueRing>
          </div>
        ) : (
          <NumericEditor hsva={hsva} mode={displayMode} onCommit={commitDisplay} />
        )}
      </div>
    </section>
  );
};

/* ─── 数値編集 (RGB / HSV / HSL、色グラデ横バー・色成分のみ・A 欄なし) ───────
 *
 * 各チャンネルを「そのチャンネルの色グラデを track 背景に持つ横スライダ」にする。
 * native `<input type="range">` で drag / キーボード / aria を標準で得る。track の
 * `linear-gradient` は `buildChannelBars` (純関数) が現在値から計算し、inline CSS 変数
 * `--bar-gradient` で渡す (styling は styles.css `.modern-channel-bar`)。
 */
const NumericEditor = ({
  hsva,
  mode,
  onCommit,
}: {
  readonly hsva: HsvaColor;
  readonly mode: NumericMode;
  readonly onCommit: (next: DisplayValues) => void;
}): React.ReactNode => {
  const d = toDisplayValues(hsva, mode);
  const bars = buildChannelBars(d);

  // スロット (VIEW_SLOT_MIN_HEIGHT) 高いっぱいに広げ、3 チャンネル行を上下均等配置に。
  return (
    <div className="flex flex-1 flex-col justify-evenly">
      {bars.map((bar) => (
        <ChannelBar key={bar.key} bar={bar} onCommit={onCommit} />
      ))}
    </div>
  );
};

/* ─── 1 チャンネル分の色グラデ横バー + 数値入力 ───────────────────────────── */
const ChannelBar = ({
  bar,
  onCommit,
}: {
  readonly bar: ReturnType<typeof buildChannelBars>[number];
  readonly onCommit: (next: DisplayValues) => void;
}): React.ReactNode => {
  // number 入力のローカル文字列 (編集途中の空文字等を許す)。確定 / 外部値変更で bar.value に同期。
  const [text, setText] = useState<string>(() => String(bar.value));
  useEffect(() => {
    setText(String(bar.value));
  }, [bar.value]);

  const clampToRange = (n: number): number => Math.max(bar.min, Math.min(bar.max, Math.round(n)));

  const commitText = (raw: string): void => {
    const n = Number(raw);
    if (Number.isFinite(n)) {
      const v = clampToRange(n);
      onCommit(bar.build(v));
      setText(String(v));
    } else {
      setText(String(bar.value)); // invalid は現在値に戻す
    }
  };

  return (
    <div className="flex items-center gap-1.5">
      <span
        className="w-3 shrink-0 select-none text-[10px] font-bold text-[var(--color-text-muted)]"
        aria-hidden="true"
      >
        {bar.label}
      </span>
      <input
        type="range"
        className="modern-channel-bar min-w-0 flex-1"
        // track 背景に当てるグラデを CSS 変数で渡す (styles.css が ::track で読む)。
        style={{ '--bar-gradient': bar.gradient } as React.CSSProperties}
        min={bar.min}
        max={bar.max}
        step={1}
        value={bar.value}
        // ドラッグ中ライブ反映 (input イベントごとに即 commit)。
        onChange={(e) => onCommit(bar.build(clampToRange(Number(e.currentTarget.value))))}
        aria-label={bar.ariaLabel}
        aria-valuetext={`${bar.value}`}
      />
      <input
        type="number"
        className="modern-channel-num w-10 shrink-0 rounded border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-1 py-0.5 text-right font-mono text-[10px] text-white outline-none focus:border-[var(--color-accent)]"
        min={bar.min}
        max={bar.max}
        step={1}
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          commitText(e.currentTarget.value);
        }}
        onBlur={(e) => commitText(e.currentTarget.value)}
        aria-label={`${bar.ariaLabel} の数値`}
      />
    </div>
  );
};
